import {Injectable} from '@angular/core';
import {HttpResponse} from '@angular/common/http';
import {
  ASSIGN_CUSTOMER_USER,
  CLIENT_MANAGER_SHOW,
  SHOW_ACCOUNTING,
  SHOW_PROJECT_FACTOR_ADD,
  SHOW_PROJECT_MNG,
  SHOW_PROJECT_PHASE_PROGRESS,
  SHOW_SALES_PARAM,
  SHOW_SETTING,
  SHOW_SMS,
  SHOW_STORE,
  SHOW_SUPPORT,
  SHOW_TICKET,
  SHOW_VOIP,
  TRANSACTION_DESCRIPTION_REQUIRED
} from '../app.constants';
import {SettingService} from '../entities/setting/setting.service';
import {ISetting} from '../shared/model/setting.model';

export interface FeatureFlags {
  showTicket: boolean;
  showStore: boolean;
  showSupport: boolean;
  showVoip: boolean;
  showProjectMng: boolean;
  showAccounting: boolean;
  showSms: boolean;
  showSetting: boolean;
  showSalesParam: boolean;
  clientManagerShow: boolean;
  assignCustomerUser: boolean;
  showProjectPhaseProgress: boolean;
  showProjectFactorAdd: boolean;
  transactionDescriptionRequired: boolean;
}

@Injectable({providedIn: 'root'})
export class FeatureFlagsService {
  flags: FeatureFlags = this.defaults();
  loaded = false;

  constructor(private settingService: SettingService) {
    this.load();
  }

  private defaults(): FeatureFlags {
    return {
      showTicket: !!SHOW_TICKET,
      showStore: !!SHOW_STORE,
      showSupport: !!SHOW_SUPPORT,
      showVoip: !!SHOW_VOIP,
      showProjectMng: !!SHOW_PROJECT_MNG,
      showAccounting: !!SHOW_ACCOUNTING,
      showSms: !!SHOW_SMS,
      showSetting: !!SHOW_SETTING,
      showSalesParam: !!SHOW_SALES_PARAM,
      clientManagerShow: !!CLIENT_MANAGER_SHOW,
      assignCustomerUser: !!ASSIGN_CUSTOMER_USER,
      showProjectPhaseProgress: !!SHOW_PROJECT_PHASE_PROGRESS,
      showProjectFactorAdd: !!SHOW_PROJECT_FACTOR_ADD,
      transactionDescriptionRequired: !!TRANSACTION_DESCRIPTION_REQUIRED
    };
  }

  load() {
    this.settingService.findGeneral().subscribe(
      (res: HttpResponse<ISetting>) => {
        this.apply(res.body);
        this.loaded = true;
      },
      () => {
        this.flags = this.defaults();
        this.loaded = true;
      }
    );
  }

  apply(setting: ISetting) {
    const base = this.defaults();
    if (!setting) {
      this.flags = base;
      return;
    }
    this.flags = {
      showTicket: this.pick(setting.feat_show_ticket, base.showTicket),
      showStore: this.pick(setting.feat_show_store, base.showStore),
      showSupport: this.pick(setting.feat_show_support, base.showSupport),
      showVoip: this.pick(setting.feat_show_voip, base.showVoip),
      showProjectMng: this.pick(setting.feat_show_project_mng, base.showProjectMng),
      showAccounting: this.pick(setting.feat_show_accounting, base.showAccounting),
      showSms: this.pick(setting.feat_show_sms, base.showSms),
      showSetting: this.pick(setting.feat_show_setting, base.showSetting),
      showSalesParam: this.pick(setting.feat_show_sales_param, base.showSalesParam),
      clientManagerShow: this.pick(setting.feat_client_manager_show, base.clientManagerShow),
      assignCustomerUser: this.pick(setting.feat_assign_customer_user, base.assignCustomerUser),
      showProjectPhaseProgress: this.pick(setting.feat_show_project_phase_progress, base.showProjectPhaseProgress),
      showProjectFactorAdd: this.pick(setting.feat_show_project_factor_add, base.showProjectFactorAdd),
      transactionDescriptionRequired: this.pick(setting.feat_transaction_description_required, base.transactionDescriptionRequired)
    };
  }

  private pick(value: any, fallback: boolean): boolean {
    if (value === null || value === undefined) {
      return fallback;
    }
    return !!value;
  }

  ensureDefaultsOn(setting: ISetting): ISetting {
    if (!setting) {
      setting = {};
    }
    const d = this.defaults();
    if (setting.feat_show_ticket == null) { setting.feat_show_ticket = d.showTicket; }
    if (setting.feat_show_store == null) { setting.feat_show_store = d.showStore; }
    if (setting.feat_show_support == null) { setting.feat_show_support = d.showSupport; }
    if (setting.feat_show_voip == null) { setting.feat_show_voip = d.showVoip; }
    if (setting.feat_show_project_mng == null) { setting.feat_show_project_mng = d.showProjectMng; }
    if (setting.feat_show_accounting == null) { setting.feat_show_accounting = d.showAccounting; }
    if (setting.feat_show_sms == null) { setting.feat_show_sms = d.showSms; }
    if (setting.feat_show_setting == null) { setting.feat_show_setting = d.showSetting; }
    if (setting.feat_show_sales_param == null) { setting.feat_show_sales_param = d.showSalesParam; }
    if (setting.feat_client_manager_show == null) { setting.feat_client_manager_show = d.clientManagerShow; }
    if (setting.feat_assign_customer_user == null) { setting.feat_assign_customer_user = d.assignCustomerUser; }
    if (setting.feat_show_project_phase_progress == null) { setting.feat_show_project_phase_progress = d.showProjectPhaseProgress; }
    if (setting.feat_show_project_factor_add == null) { setting.feat_show_project_factor_add = d.showProjectFactorAdd; }
    if (setting.feat_transaction_description_required == null) { setting.feat_transaction_description_required = d.transactionDescriptionRequired; }
    return setting;
  }
}
